import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { unwrap } from '@/api/envelope';
import {
  updateOrganization,
  type NotificationAllowance,
  type OrganizationResponse,
} from '@/api/generated';
import { applyServerErrors } from '@/api/serverErrors';
import { UnsavedChangesDialog } from '@/components/form/UnsavedChangesDialog';
import { useUnsavedChanges } from '@/components/form/useUnsavedChanges';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { useToast } from '@/components/ui/useToast';

import { storeSavedOrganization } from './organizationCache';

/** Blank, or a whole number of 0 or more - the two things the backend takes. */
const allowanceSchema = z.object({
  override: z
    .string()
    .trim()
    .refine((value) => value === '' || /^\d+$/.test(value), {
      message: 'Enter a whole number of 0 or more, or leave it blank',
    }),
});

type AllowanceValues = z.infer<typeof allowanceSchema>;

/** The server's one field name for this card to the form's one field. */
const SERVER_FIELDS = { monthly_notification_limit: 'override' } as const;

function toValues(record: OrganizationResponse): AllowanceValues {
  const override = record.monthly_notification_limit;
  return {
    override: override === null || override === undefined ? '' : String(override),
  };
}

/** The allowance that applies, in words. */
function allowanceText(effective: number | null | undefined): string {
  if (effective === null || effective === undefined) return 'Not set';
  if (effective === 0) return 'No notifications';
  return `${effective} ${effective === 1 ? 'notification' : 'notifications'} a month`;
}

/**
 * The month a usage count covers, as people say it: "September 2026". Named
 * from the server's period and read at UTC, because the backend's months are
 * UTC - at 21:00 on 31 August in New York it is already September's count.
 */
function periodText(periodStart: string): string {
  return new Intl.DateTimeFormat(undefined, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${periodStart}T00:00:00Z`));
}

/**
 * This month's usage, when the record carries usage counted against the limit
 * the card is showing. After a save that changes the limit, the usage in hand
 * was counted against the old one until the refetch lands.
 */
function currentUsage(record: OrganizationResponse): NotificationAllowance | null {
  const usage = record.notification_allowance;
  if (!usage) return null;
  return usage.limit === record.effective_monthly_notification_limit ? usage : null;
}

interface NotificationAllowanceCardProps {
  record: OrganizationResponse;
}

/**
 * How many push notifications the organization may send each month, how many
 * it has used and has left this month, and the override staff can set.
 *
 * Blank and zero are different things and the card says so: blank means "use
 * the platform default", zero means "none at all". That is the backend's own
 * distinction, a null override against a stored 0.
 *
 * Used and remaining are shown as the backend reports them. Used can be above
 * the limit when staff lower it after sending, and the card shows that as it
 * is ("6 of 4") beside none remaining. When there is no usage to trust, the
 * card says so rather than showing a zero that would read as "none sent".
 */
export function NotificationAllowanceCard({ record }: NotificationAllowanceCardProps) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [failure, setFailure] = useState<string | null>(null);
  const alertRef = useRef<HTMLDivElement>(null);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    setFocus,
    formState: { errors, isDirty },
  } = useForm<AllowanceValues>({
    resolver: zodResolver(allowanceSchema),
    defaultValues: toValues(record),
  });

  useEffect(() => {
    if (failure) alertRef.current?.focus();
  }, [failure]);

  // The same flag the Save button reads, so the prompt and the button cannot
  // disagree about whether anything is unsaved.
  const unsaved = useUnsavedChanges(isDirty);

  const save = useMutation({
    mutationFn: async ({ override }: AllowanceValues) => {
      const reply = await updateOrganization({
        path: { organization_id: record.id },
        body: { monthly_notification_limit: override === '' ? null : Number(override) },
        throwOnError: true,
      });
      return unwrap(reply.data);
    },
    onMutate: () => setFailure(null),
    onSuccess: async (saved) => {
      reset(toValues(saved));
      storeSavedOrganization(queryClient, saved);
      toast.show({ message: 'Notification allowance saved.' });
      await queryClient.invalidateQueries({ queryKey: ['staff', 'organizations'] });
    },
    onError: (error) => {
      // A refusal naming the limit lands on the Override box; anything else
      // goes to the alert above the form.
      const { remainder } = applyServerErrors(
        error,
        { setError, setFocus },
        SERVER_FIELDS,
      );
      if (remainder) setFailure(remainder);
    },
  });

  const hasOverride =
    record.monthly_notification_limit !== null &&
    record.monthly_notification_limit !== undefined;
  const usage = currentUsage(record);

  return (
    <Stack spacing={2} data-testid="allowance-section">
      <Box>
        <Typography variant="body2" color="text.secondary">
          Applies this month
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontWeight: 600 }} data-testid="allowance-current-value">
            {allowanceText(record.effective_monthly_notification_limit)}
          </Typography>
          <Chip
            size="small"
            variant="outlined"
            label={hasOverride ? 'Override' : 'Platform default'}
            data-testid="allowance-source-chip"
          />
        </Box>
      </Box>

      {usage ? (
        <Box>
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Used this month
              </Typography>
              <Typography sx={{ fontWeight: 600 }} data-testid="allowance-used-value">
                {usage.used} of {usage.limit}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Remaining
              </Typography>
              <Typography
                sx={{ fontWeight: 600 }}
                data-testid="allowance-remaining-value"
              >
                {usage.remaining}
              </Typography>
            </Box>
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            data-testid="allowance-period-caption"
          >
            {periodText(usage.period_start)}, counted in UTC. A scheduled notification
            counts as used as soon as it is scheduled.
          </Typography>
        </Box>
      ) : (
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid="allowance-usage-empty"
        >
          This month’s usage is not available right now.
        </Typography>
      )}

      {failure ? (
        <Alert
          severity="error"
          role="alert"
          ref={alertRef}
          tabIndex={-1}
          data-testid="allowance-alert"
        >
          {failure}
        </Alert>
      ) : null}

      <Stack
        component="form"
        spacing={2}
        aria-label="Notification allowance"
        data-testid="allowance-form"
        noValidate
        onSubmit={(event) => {
          void handleSubmit((values) => save.mutate(values))(event);
        }}
      >
        <Field
          label="Override"
          hint="Leave blank to use the platform default. 0 means none."
          error={errors.override?.message}
          testId="allowance-override"
        >
          <TextField
            {...register('override')}
            size="small"
            // Text with a numeric keyboard, not type="number": a number box
            // swallows "1.5" and "-1" into an empty value, and the person
            // would be told nothing about what they typed.
            slotProps={{ htmlInput: { inputMode: 'numeric' } }}
          />
        </Field>
        <Box>
          <Button
            type="submit"
            disabled={!isDirty}
            loading={save.isPending}
            data-testid="allowance-save-button"
          >
            Save allowance
          </Button>
        </Box>
      </Stack>

      <UnsavedChangesDialog {...unsaved} />
    </Stack>
  );
}
